import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Music, Book, Globe, ArrowRight } from 'lucide-react';

const Indiana: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Indiana University";

    return (
        <div className="min-h-screen bg-white font-sans text-[#990000] selection:bg-[#990000] selection:text-white">
            {/* Top Bar - Cream */}
            <div className="bg-[#EDEBEB] text-[#333] py-1.5 px-6 flex justify-end gap-6 text-[11px] font-bold uppercase tracking-wider">
                <a href="#" className="hover:underline">One.IU</a>
                <a href="#" className="hover:underline">Canvas</a>
                <a href="#" className="hover:underline">Directory</a>
                <a href="#" className="hover:underline">Maps</a>
            </div>

            {/* Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 border-b border-gray-200">
                <div className="max-w-[1240px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                         <div className="text-[#990000] font-serif font-black text-4xl leading-none">{schoolName.charAt(0)}</div>
                        <div className="flex flex-col">
                            <span className="font-sans text-xl font-bold text-[#990000] uppercase tracking-wide leading-none">{schoolName}</span>
                            <span className="font-sans text-xs font-bold text-[#555] uppercase tracking-wider leading-none">Bloomington</span>
                        </div>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#333] text-sm uppercase tracking-wide">
                        <a href="#" className="hover:text-[#990000] transition-colors border-b-2 border-transparent hover:border-[#990000] pb-1">Admissions</a>
                        <a href="#" className="hover:text-[#990000] transition-colors border-b-2 border-transparent hover:border-[#990000] pb-1">Academics</a>
                        <a href="#" className="hover:text-[#990000] transition-colors border-b-2 border-transparent hover:border-[#990000] pb-1">Research</a>
                        <a href="#" className="hover:text-[#990000] transition-colors border-b-2 border-transparent hover:border-[#990000] pb-1">Student Life</a>
                        <a href="#" className="hover:text-[#990000] transition-colors border-b-2 border-transparent hover:border-[#990000] pb-1">News</a>
                    </div>
                    
                    <div className="flex gap-4 items-center">
                         <div className="bg-[#990000] text-white p-2 rounded-sm cursor-pointer hover:bg-[#7a0000]">
                            <Search size={18} />
                         </div>
                         <Menu className="cursor-pointer lg:hidden text-[#990000]" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - Sample Gates */}
            <header className="relative h-[650px]">
                <img 
                    src="https://images.unsplash.com/photo-1596483584347-7521c7dc40fe?q=80&w=2070&auto=format&fit=crop" 
                    alt="Sample Gates" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                 {/* Cream bottom fade */}
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#EDEBEB] to-transparent"></div>

                <div className="absolute bottom-0 left-0 w-full p-12 md:p-20 text-center">
                     <div className="max-w-3xl mx-auto text-[#990000] bg-white/90 backdrop-blur-sm p-12 rounded-sm shadow-xl">
                         <h1 className="text-4xl md:text-6xl font-serif font-black mb-6 uppercase tracking-wide">
                             Fulfilling the Promise.
                         </h1>
                         <p className="text-lg md:text-xl mb-8 font-medium text-[#333]">
                             We are committed to excellence in research, teaching, and service.
                         </p>
                         <Button className="bg-[#990000] hover:bg-[#7a0000] text-white font-bold uppercase tracking-widest px-10 h-14 rounded-sm shadow-md">
                             Apply to {schoolName.split(' ')[0]}
                         </Button>
                     </div>
                </div>
            </header>

            {/* Pillars */}
            <section className="bg-[#EDEBEB] py-24 px-6 md:px-12">
                <div className="max-w-[1240px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center text-[#990000]">
                     <div className="bg-white p-8 shadow-sm border-t-8 border-[#990000] hover:-translate-y-2 transition-transform duration-300">
                         <Music size={48} className="mx-auto mb-6" />
                         <h3 className="text-2xl font-bold mb-4 uppercase">Arts</h3>
                         <p className="text-gray-600">World-Renowned Jacobs School of Music</p>
                     </div>
                      <div className="bg-white p-8 shadow-sm border-t-8 border-[#990000] hover:-translate-y-2 transition-transform duration-300">
                         <Book size={48} className="mx-auto mb-6" />
                         <h3 className="text-2xl font-bold mb-4 uppercase">Business</h3>
                         <p className="text-gray-600">Top-Ranked Kelley School of Business</p>
                     </div>
                      <div className="bg-white p-8 shadow-sm border-t-8 border-[#990000] hover:-translate-y-2 transition-transform duration-300">
                         <Globe size={48} className="mx-auto mb-6" />
                         <h3 className="text-2xl font-bold mb-4 uppercase">Global</h3>
                         <p className="text-gray-600">Leaders in International Studies</p>
                     </div>
                </div>
            </section>

             {/* News List */}
             <section className="py-24 px-6 md:px-12 bg-white">
                 <div className="max-w-[1240px] mx-auto">
                     <div className="flex justify-between items-end mb-12 border-b-2 border-[#990000] pb-4">
                         <h2 className="text-3xl font-serif font-black text-[#990000] uppercase">News at {schoolName.split(' ')[0]}</h2>
                         <a href="#" className="font-bold text-[#333] text-xs uppercase hover:underline mb-2">View Newsroom</a>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                         {[
                             "IU researchers advance understanding of AI ethics",
                             "New museum of art and archaeology opens its doors",
                             "Kelley Direct Online MBA ranked #1 in the nation",
                             "Hoosier basketball prepares for March Madness"
                         ].map((title, i) => (
                             <div key={i} className="group cursor-pointer">
                                 <div className="h-48 bg-gray-100 mb-4 overflow-hidden relative">
                                     <div className="absolute bottom-0 left-0 bg-[#990000] text-white text-[10px] font-bold uppercase px-2 py-1">Featured</div>
                                     <img src={`https://source.unsplash.com/random/400x300?sig=${i+140}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt="News"/>
                                 </div>
                                 <h3 className="text-lg font-bold text-[#333] group-hover:text-[#990000] transition-colors mb-2 leading-snug">{title}</h3>
                                  <div className="flex items-center gap-2 text-[#990000] font-bold uppercase text-xs tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                                     Read Story <ArrowRight size={14}/>
                                 </div>
                             </div>
                         ))}
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#990000] text-white py-16 px-6 md:px-12">
                  <div className="max-w-[1240px] mx-auto flex flex-col md:flex-row justify-between gap-12 font-sans text-sm">
                      <div className="max-w-xs">
                           <div className="flex items-center gap-3 mb-6">
                                <span className="text-white font-serif font-black text-3xl leading-none">{schoolName.charAt(0)}</span>
                               <span className="font-sans font-bold text-white text-xl uppercase tracking-wide">{schoolName}</span>
                           </div>
                          <p className="mb-6 opacity-90">
                              Bloomington, IN 47405 <br/>
                              812-855-4848
                          </p>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 font-bold uppercase text-xs tracking-widest opacity-90">
                          <ul className="space-y-4 hover:[&_a]:text-[#EDEBEB]">
                              <li><a href="#">Accessibility</a></li>
                              <li><a href="#">Privacy Notice</a></li>
                              <li><a href="#">Copyright</a></li>
                          </ul>
                          <ul className="space-y-4 hover:[&_a]:text-[#EDEBEB]">
                               <li><a href="#">Careers</a></li>
                              <li><a href="#">Contact</a></li>
                              <li><a href="#">Emergency</a></li>
                          </ul>
                      </div>
                  </div>
                  <div className="max-w-[1240px] mx-auto mt-16 text-center text-xs text-white/60 border-t border-white/20 pt-8">
                      © 2026 The Trustees of {schoolName}
                  </div>
             </footer>
        </div>
    );
};

export default Indiana;
